import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import DocumentationMS from '../../api/documentation';
import { markdownOptions } from '../../helpers/markdownOptions';
import useApiQuery from '../../hooks/useApiQuery';
import Modal from '../Modal';

interface GitlabWikiPageContent {
  title: string;
  content: string;
  format: string;
  slug: string;
}

interface ParsedWikiContent {
  title?: string;
  content: string;
}

interface GitlabWikiHelpButtonProps {
  slug: string;
  tooltipMessageId?: string;
  tooltipDefaultMessage?: string;
  modalTitleMessageId?: string;
  modalTitleDefaultMessage?: string;
  buttonAriaLabel?: string;
  buttonSx?: SxProps<Theme>;
  modalWidth?: string;
  modalHeight?: string;
}

const helpButtonBaseSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'primary.main',
  mt: 0.5,
};

const helpContentBoxSx: SxProps<Theme> = {
  pr: 1,
};

const loadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  py: 6,
};

const helpMarkdownOptions = {
  ...markdownOptions,
  overrides: {
    ...markdownOptions.overrides,
    img: {
      props: {
        style: {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          margin: '16px auto',
          borderRadius: '8px',
        },
      },
    },
  },
} as MarkdownToJSX.Options;

type SxPropsArray = Extract<SxProps<Theme>, readonly unknown[]>;

const isSxPropsArray = (sx: SxProps<Theme>): sx is SxPropsArray => Array.isArray(sx);

const mergeButtonSx = (buttonSx?: SxProps<Theme>): SxProps<Theme> => {
  if (buttonSx === undefined) {
    return helpButtonBaseSx;
  }

  if (isSxPropsArray(buttonSx)) {
    return [helpButtonBaseSx, ...buttonSx];
  }

  return [helpButtonBaseSx, buttonSx];
};

const stripGitlabImageMetadata = (content: string): string => {
  return content
    .replace(/(?!^)\s*\{[^{}\n]*(?:width|height)\s*=\s*[^{}\n]+[^{}\n]*\}(?=\s*(?:\n|$))/gim, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const parseWikiFrontmatter = (content: string): ParsedWikiContent => {
  const frontmatterMatch = /^---\s*([\s\S]*?)\s*---\s*/.exec(content);

  if (!frontmatterMatch) {
    return { content: stripGitlabImageMetadata(content) };
  }

  const frontmatter = frontmatterMatch[1] ?? '';
  const titleMatch = /(?:^|\n)\s*title:\s*(.+?)\s*(?:\n|$)/i.exec(frontmatter);
  const parsedTitle = titleMatch?.[1]?.trim().replace(/^['"]|['"]$/g, '');
  const normalizedContent = stripGitlabImageMetadata(
    content.slice(frontmatterMatch[0].length).trimStart()
  );

  if (!parsedTitle) {
    return { content: normalizedContent };
  }

  return {
    title: parsedTitle,
    content: normalizedContent,
  };
};

const GitlabWikiHelpButton = ({
  slug,
  tooltipMessageId = '',
  tooltipDefaultMessage = '',
  modalTitleMessageId = '',
  modalTitleDefaultMessage = '',
  buttonAriaLabel = 'Open help guide',
  buttonSx,
  modalWidth = 'min(960px, 92vw)',
  modalHeight = 'min(80vh, 900px)',
}: GitlabWikiHelpButtonProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: helpContent,
    error: helpContentError,
    isLoading: isHelpContentLoading,
    makeApiCall: getHelpContent,
  } = useApiQuery<GitlabWikiPageContent>(
    DocumentationMS.getGitlabWikiPage.bind(DocumentationMS) as unknown as (
      ...args: unknown[]
    ) => Promise<GitlabWikiPageContent>,
    {
      disableGlobalLoader: true,
    }
  );
  const parsedHelpContent = parseWikiFrontmatter(helpContent?.content ?? '');

  useEffect(() => {
    if (!isOpen || helpContent || isHelpContentLoading) {
      return;
    }

    void getHelpContent(slug);
  }, [getHelpContent, helpContent, isHelpContentLoading, isOpen, slug]);

  const formatOptionalMessage = (messageId: string, defaultMessage: string): string => {
    if (!messageId && !defaultMessage) {
      return '';
    }

    return intl.formatMessage({
      id: messageId,
      defaultMessage,
    });
  };

  const tooltipLabel = formatOptionalMessage(tooltipMessageId, tooltipDefaultMessage);
  const modalTitle =
    parsedHelpContent.title ??
    helpContent?.title ??
    formatOptionalMessage(modalTitleMessageId, modalTitleDefaultMessage);
  const resolvedButtonSx = mergeButtonSx(buttonSx);

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <IconButton
          aria-label={buttonAriaLabel}
          color='primary'
          onClick={() => {
            setIsOpen(true);
          }}
          sx={resolvedButtonSx}
        >
          <HelpOutlineRoundedIcon />
        </IconButton>
      </Tooltip>

      <Modal
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={modalTitle}
        width={modalWidth}
        height={modalHeight}
      >
        <Box sx={helpContentBoxSx}>
          {isHelpContentLoading ? (
            <Box sx={loadingContainerSx}>
              <CircularProgress />
            </Box>
          ) : null}

          {!isHelpContentLoading && helpContentError ? (
            <Typography color='error'>{helpContentError.message}</Typography>
          ) : null}

          {!isHelpContentLoading && helpContent ? (
            <Markdown options={helpMarkdownOptions}>{parsedHelpContent.content}</Markdown>
          ) : null}
        </Box>
      </Modal>
    </>
  );
};

export default GitlabWikiHelpButton;
